/**
 * BLOQUE 4 — Reportes por metodología generados en background, secuencial.
 *
 * Props:
 *   caseId, selected[], hypotheses[], reload(), existingReports{}
 *
 * Pone botón "Iniciar Investigación con Metodologías Seleccionadas" que
 * dispara jobs uno-por-uno y muestra progreso + reporte estructurado al
 * terminar cada metodología.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { slugForMethodology } from "../constants/methodology_steps";
import {
  Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  FileText, Download,
} from "lucide-react";

const CONFIDENCE_COLOR = { Alto: "#00C853", Medio: "#FB8C00", Bajo: "#90A4AE" };

export default function MethodologyBackgroundReports({
  caseId, selected, hypotheses, reload, existingReports,
}) {
  const [jobs, setJobs] = useState({}); // {slug: {job_id, status, progress_pct, current_step, error}}
  const [running, setRunning] = useState(false);
  const queueRef = useRef([]);
  const pollRef = useRef(null);

  const reports = existingReports || {};

  function resolveSlug(name, customs = []) {
    const std = slugForMethodology(name);
    if (std) return std;
    const cm = customs.find((m) => m.name === name);
    return cm?.slug || (cm ? `custom_${cm.id}` : null);
  }

  async function startQueue(customs) {
    setRunning(true);
    const slugs = selected
      .map((n) => ({ name: n, slug: resolveSlug(n, customs) }))
      .filter((x) => x.slug);
    queueRef.current = [...slugs];
    setJobs({});
    await runNext();
  }

  async function runNext() {
    if (queueRef.current.length === 0) {
      setRunning(false);
      await reload();
      return;
    }
    const { name, slug } = queueRef.current.shift();
    setJobs((prev) => ({
      ...prev, [slug]: { name, status: "processing", progress_pct: 0, current_step: "Iniciando..." },
    }));
    try {
      const r = await api.post(`/investigations/${caseId}/run-analysis`, {
        kind: "methodology",
        payload: { slug, hypotheses },
      });
      const job_id = r.data.job_id;
      setJobs((prev) => ({ ...prev, [slug]: { ...prev[slug], job_id } }));
      await pollUntilDone(slug, job_id);
    } catch (e) {
      setJobs((prev) => ({
        ...prev,
        [slug]: { ...(prev[slug] || {}), status: "failed", error: e?.message || "Error" },
      }));
    }
    // continuar con la siguiente
    runNext();
  }

  function pollUntilDone(slug, job_id) {
    return new Promise((resolve) => {
      if (pollRef.current) clearInterval(pollRef.current);
      const tick = async () => {
        try {
          const r = await api.get(`/investigations/${caseId}/analysis-status/${job_id}`);
          setJobs((prev) => ({ ...prev, [slug]: { ...prev[slug], ...r.data } }));
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            resolve();
          }
        } catch (e) {
          setJobs((prev) => ({
            ...prev,
            [slug]: { ...prev[slug], status: "failed", error: e.message },
          }));
          clearInterval(pollRef.current);
          pollRef.current = null;
          resolve();
        }
      };
      tick();
      pollRef.current = setInterval(tick, 4000);
    });
  }

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  async function downloadDocx(slug) {
    try {
      const r = await api.post(
        `/investigations/${caseId}/methodology-report/${encodeURIComponent(slug)}/docx`,
        {}, { responseType: "blob" },
      );
      triggerDownload(r.data, `${caseId}_${slug}_reporte.docx`, "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  }

  async function downloadPdf(slug) {
    try {
      const r = await api.post(
        `/investigations/${caseId}/methodology-report/${encodeURIComponent(slug)}/pdf`,
        {}, { responseType: "blob" },
      );
      triggerDownload(r.data, `${caseId}_${slug}_reporte.pdf`, "application/pdf");
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  }

  function triggerDownload(blob, name, type) {
    const url = URL.createObjectURL(new Blob([blob], { type }));
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  }

  return (
    <div data-testid="psc-mbr-root">
      <div className="card mt-2" style={{ background: "var(--bg-2)" }}>
        <div className="flex items-center justify-between">
          <div>
            <b>Investigación con Metodologías Seleccionadas</b>
            <div className="text-xs text-dim">
              Cada metodología se ejecuta en background con la línea de tiempo,
              las hipótesis y la evidencia consolidada.
            </div>
          </div>
          <button
            className="btn btn-primary"
            disabled={running || selected.length < 1}
            onClick={async () => {
              const cm = await api.get("/methodologies").then((r) => r.data || []).catch(() => []);
              startQueue(cm);
            }}
            data-testid="psc-mbr-start"
          >
            {running ? <Loader2 size={12} className="spin"/> : <CheckCircle size={12}/>} Iniciar Investigación con {selected.length} Metodología(s)
          </button>
        </div>
      </div>

      {/* Progreso por metodología activa */}
      {Object.entries(jobs).map(([slug, j]) => (
        <div key={`job-${slug}`} className="card mt-2" data-testid={`psc-mbr-job-${slug}`}>
          <div className="flex items-center gap-2">
            {j.status === "processing" && <Loader2 size={14} className="spin"/>}
            {j.status === "completed" && <CheckCircle size={14} style={{ color: "#00C853" }}/>}
            {j.status === "failed" && <AlertCircle size={14} style={{ color: "#D50000" }}/>}
            <b>{j.name || slug}</b>
            <span className="text-xs text-dim">{j.progress_pct || 0}%</span>
          </div>
          <div style={{ background: "var(--bg-2)", borderRadius: 4, height: 6, overflow: "hidden", marginTop: 4 }}>
            <div style={{
              width: `${j.progress_pct || 0}%`,
              height: "100%",
              background: j.status === "failed" ? "#D50000" : (j.status === "completed" ? "#00C853" : "var(--accent)"),
              transition: "width 0.4s ease",
            }}/>
          </div>
          <div className="text-xs text-dim mt-1">{j.current_step || ""}</div>
          {j.error && <div className="text-xs" style={{ color: "#D50000" }}>{j.error}</div>}
        </div>
      ))}

      {/* Reportes ya generados */}
      {Object.values(reports).length > 0 && (
        <div className="mt-2 space-y-2">
          {Object.values(reports).map((rep) => (
            <ReportAccordion
              key={`rep-${rep.slug}`}
              rep={rep}
              onDocx={() => downloadDocx(rep.slug)}
              onPdf={() => downloadPdf(rep.slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportAccordion({ rep, onDocx, onPdf }) {
  const [open, setOpen] = useState(false);
  const r = rep.report || {};
  const rootCauses = r.root_causes || [];
  return (
    <div className="card" data-testid={`psc-report-${rep.slug}`}>
      <div className="flex items-center justify-between cursor-pointer"
           onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
          <b>{rep.methodology_name}</b>
          <span style={{
            background: "#00C853", color: "#FFF",
            padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
          }}>✓ Completada</span>
          <span className="text-xs text-dim">
            {rootCauses.length} causa(s) raíz
          </span>
        </div>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm" onClick={onDocx} data-testid={`psc-report-docx-${rep.slug}`}>
            <Download size={11}/> Word
          </button>
          <button className="btn btn-sm" onClick={onPdf} data-testid={`psc-report-pdf-${rep.slug}`}>
            <Download size={11}/> PDF
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-2">
          {r.executive_summary && (
            <div className="card mt-2" style={{ background: "rgba(21,101,192,0.10)", borderColor: "#1565C0" }}>
              <div className="card-title">Resumen Ejecutivo</div>
              <p className="text-sm">{r.executive_summary}</p>
            </div>
          )}
          {(r.steps || []).map((s, i) => (
            <details key={`stp-${i}`} className="mt-2">
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                Paso {s.step_number}: {s.step_name}
              </summary>
              <div style={{ padding: "6px 12px" }}>
                <p className="text-sm">{s.analysis}</p>
                {(s.findings || []).length > 0 && (
                  <div className="text-xs mt-1">
                    <b>📌 Hallazgos:</b>
                    <ul style={{ paddingLeft: 16 }}>
                      {s.findings.map((f, j) => <li key={`f-${i}-${j}`}>{f}</li>)}
                    </ul>
                  </div>
                )}
                {(s.evidence_references || []).length > 0 && (
                  <div className="text-xs mt-1">
                    <b>📎 Evidencia:</b>
                    <ul style={{ paddingLeft: 16 }}>
                      {s.evidence_references.map((e, j) => <li key={`e-${i}-${j}`}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          ))}
          {rootCauses.length > 0 && (
            <div className="card mt-2">
              <div className="card-title">Causas Raíz</div>
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr><th style={{textAlign:"left"}}>Causa</th><th>Confianza</th></tr>
                </thead>
                <tbody>
                  {rootCauses.map((rc, i) => (
                    <tr key={`rc-${i}`}>
                      <td>{typeof rc === "string" ? rc : rc.cause}</td>
                      <td>
                        <span style={{
                          background: CONFIDENCE_COLOR[rc.confidence] || "#90A4AE",
                          color: "#FFF", padding: "2px 8px", borderRadius: 10,
                          fontSize: 10, fontWeight: 700,
                        }}>{rc.confidence || "—"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.diagram_description && (
            <div className="card mt-2">
              <div className="card-title"><FileText size={12} style={{display:"inline"}}/> Diagrama Descrito</div>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 11 }}>{r.diagram_description}</pre>
            </div>
          )}
          {(r.corrective_actions || []).length > 0 && (
            <div className="card mt-2">
              <div className="card-title">Acciones Correctivas</div>
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead><tr><th style={{textAlign:"left"}}>Acción</th><th>Prioridad</th><th>Responsable</th></tr></thead>
                <tbody>
                  {r.corrective_actions.map((a, i) => (
                    <tr key={`a-${i}`}>
                      <td>{a.action}</td><td>{a.priority || "—"}</td><td>{a.responsible || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.limitations && (
            <div className="card mt-2" style={{ background: "var(--bg-2)" }}>
              <div className="card-title">Limitaciones</div>
              <p className="text-sm">{r.limitations}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
